import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DocumentPlusIcon } from '@heroicons/react/24/outline';
import {
    RichEditor,
    ChartNoteCard,
    isDocEmpty,
    collectDocImageFileIds,
    applyResolvedUrlsToDoc,
    type TiptapDoc
} from '@odo/shared-ui';
import ConfirmDeleteNoteModal from './ConfirmDeleteNoteModal';
import { EMPTY_CHART_NOTE_DOC } from './types';
import { usePatientNotes } from '../../hooks/usePatientNotes';
import { useUpdateAppointment } from '../../hooks/useAppointments';
import { MAX_NOTE_BODY_LENGTH, MAX_NOTE_TITLE_LENGTH } from '../patient/notes/noteAccess';
import {
    resolveInternalImageUrls,
    uploadInternalImage
} from '../../services/patient/internalImages';
import { docFromPlainText } from '@odo/shared-ui';

const EDITOR_MIN_HEIGHT_PX = 140;

interface ChartNotesFieldProps {
    patientId: string | null;
    /** The appointment currently being composed, when editing an existing
     *  one: needed (alongside `linkedNoteId`) to delete a persisted note. */
    appointmentId: string | null;
    /** The appointment's already-linked note id, when editing. */
    linkedNoteId: string | null;
    /** Whether an existing appointment (not a brand-new one) is being
     *  edited. Only used to pick the editor's placeholder copy. */
    isEditingAppointment: boolean;
    value: TiptapDoc;
    onChange: (doc: TiptapDoc) => void;
    title: string;
    onTitleChange: (title: string) => void;
    /** Seeds doc AND title in ONE draft update: separate onChange + onTitleChange calls would each overwrite the other from a stale snapshot. */
    onSeed: (doc: TiptapDoc, title: string) => void;
    /** Derived "Appointment: {reason} ({date})" title, mirrored into the
     *  field for a brand-new note until the doctor types their own. */
    derivedTitle: string;
    bodyLength: number;
    /** Opens straight into editing (skips the "Add chart note" button) for
     *  the agenda row's "Add chart note" nudge, so typing can start right away. */
    autoFocus?: boolean;
}

/** Three-state affordance (no note / editing / read-only `ChartNoteCard`) for
 *  the appointment's one chart note — a real patient note, fetched by linkedNoteId and mirroring NoteModal.tsx's editor wiring. */
const ChartNotesField: React.FC<ChartNotesFieldProps> = ({
    patientId,
    appointmentId,
    linkedNoteId,
    isEditingAppointment,
    value,
    onChange,
    title,
    onTitleChange,
    onSeed,
    derivedTitle,
    bodyLength,
    autoFocus
}) => {
    const {
        notes,
        isLoading: isLoadingNotes,
        isFetching: isFetchingNotes
    } = usePatientNotes(patientId ?? undefined, {
        enabled: !!linkedNoteId
    });
    const linkedNote = linkedNoteId
        ? (notes.find((note) => note.id === linkedNoteId) ?? null)
        : null;
    // A known-existing note missing from the (persisted, so isLoading is
    // already false) list means a stale cache, not "no note" — avoid the flash.
    const isWaitingForLinkedNote =
        !!linkedNoteId && !linkedNote && (isLoadingNotes || isFetchingNotes);

    const [isEditingNote, setIsEditingNote] = useState(() => !!autoFocus);
    const [titleTouched, setTitleTouched] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const deleteMutation = useUpdateAppointment();

    // Mirrors the latest onChange/value/title for async callbacks below that
    // could otherwise resolve after a re-render and close over a stale `draft`.
    const latestRef = useRef({ onChange, value, onTitleChange, onSeed, title });
    useEffect(() => {
        latestRef.current = { onChange, value, onTitleChange, onSeed, title };
    });

    // Keyed by the seeded note id, not a "ran yet" boolean: a boolean would
    // latch on a stale-cache first render and never seed the note landing later.
    const seededNoteIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (!linkedNote || seededNoteIdRef.current === linkedNote.id) return;
        seededNoteIdRef.current = linkedNote.id;
        const doc = linkedNote.bodyDoc ?? docFromPlainText(linkedNote.body);
        // One atomic update: two separate calls would each rebuild the draft
        // from the same stale snapshot, so the title would clobber the doc.
        latestRef.current.onSeed(doc, linkedNote.title);
    }, [linkedNote]);

    // Live-mirrors the derived title (not a one-shot prefill, since Reason
    // may be filled in after) until a real note exists or the doctor types their own.
    useEffect(() => {
        if (linkedNote || titleTouched) return;
        if (!isEditingNote || isWaitingForLinkedNote) return;
        if (latestRef.current.title === derivedTitle) return;
        latestRef.current.onTitleChange(derivedTitle);
    }, [linkedNote, titleTouched, isEditingNote, isWaitingForLinkedNote, derivedTitle]);

    // Swaps stored (expired) image file-id references for freshly-signed
    // URLs so previously-attached images render, same as NoteModal.tsx.
    useEffect(() => {
        if (!linkedNote?.bodyDoc || !patientId) return;
        const fileIds = collectDocImageFileIds(linkedNote.bodyDoc);
        if (fileIds.length === 0) return;
        let cancelled = false;
        resolveInternalImageUrls(patientId, fileIds)
            .then((urls) => {
                if (cancelled) return;
                const latest = latestRef.current;
                latest.onChange(applyResolvedUrlsToDoc(latest.value, urls));
            })
            .catch(() => {
                /* leave unresolved: images may render broken, but text is intact */
            });
        return () => {
            cancelled = true;
        };
    }, [linkedNote, patientId]);

    const handleImageUpload = useCallback(
        async (file: File) => {
            if (!patientId) {
                throw new Error('Select a patient before adding images.');
            }
            const result = await uploadInternalImage(patientId, file, 'note');
            return { url: result.url, fileId: result.fileId };
        },
        [patientId]
    );

    const handleDone = () => {
        if (isDocEmpty(value)) {
            onChange(EMPTY_CHART_NOTE_DOC);
            onTitleChange('');
        }
        setIsEditingNote(false);
    };

    // A persisted note needs confirmation (it deletes a real Notes-tab
    // record too); unsaved content just clears locally, no network call.
    const handleDeleteRequest = () => {
        if (linkedNoteId) {
            setConfirmingDelete(true);
        } else {
            onSeed(EMPTY_CHART_NOTE_DOC, '');
            setIsEditingNote(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (!linkedNoteId || !patientId || !appointmentId) return;
        try {
            await deleteMutation.mutateAsync({ patientId, appointmentId, note: null });
            onSeed(EMPTY_CHART_NOTE_DOC, '');
            setIsEditingNote(false);
            setConfirmingDelete(false);
        } catch {
            // The mutation's onError already surfaced a toast; keep the
            // confirm dialog open so the doctor can retry or back out.
        }
    };

    return (
        <div>
            <div className="flex items-baseline justify-between mb-1.5">
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    Chart notes
                </div>
                <span className="text-[11px] text-gray-400">
                    Saved to the patient&rsquo;s Notes tab
                </span>
            </div>
            {isWaitingForLinkedNote ? (
                <div
                    className="rounded-lg border border-gray-200 bg-gray-50 animate-pulse"
                    style={{ minHeight: EDITOR_MIN_HEIGHT_PX }}
                />
            ) : isEditingNote ? (
                <div className="space-y-2">
                    <input
                        type="text"
                        value={title}
                        maxLength={MAX_NOTE_TITLE_LENGTH}
                        onChange={(e) => {
                            setTitleTouched(true);
                            onTitleChange(e.target.value);
                        }}
                        placeholder="Short, descriptive title…"
                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                    />
                    <RichEditor
                        value={value}
                        onChange={onChange}
                        onImageUpload={handleImageUpload}
                        placeholder={
                            isEditingAppointment
                                ? 'What happened during this visit? IPR performed, aligner seated, fit checked…'
                                : 'Add after the visit. What was done, what to watch next time.'
                        }
                        minHeightPx={EDITOR_MIN_HEIGHT_PX}
                    />
                    <div className="flex items-center justify-between">
                        <span
                            className={`text-xs ${bodyLength > MAX_NOTE_BODY_LENGTH ? 'text-red-500' : 'text-gray-400'}`}
                        >
                            {bodyLength}/{MAX_NOTE_BODY_LENGTH}
                        </span>
                        <button
                            type="button"
                            onClick={handleDone}
                            className="rounded-lg border border-gray-200 px-3 py-1.5 text-[12.5px] font-medium text-gray-600 hover:bg-gray-50"
                        >
                            Done
                        </button>
                    </div>
                </div>
            ) : !isDocEmpty(value) ? (
                <ChartNoteCard
                    title={title}
                    doc={value}
                    onEdit={() => setIsEditingNote(true)}
                    onDelete={handleDeleteRequest}
                />
            ) : (
                <button
                    type="button"
                    onClick={() => setIsEditingNote(true)}
                    className="w-full flex items-center justify-center gap-2 rounded-lg border border-gray-200 px-4 py-2.5 text-[13px] font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                >
                    <DocumentPlusIcon className="h-4 w-4" />
                    Add chart note
                </button>
            )}
            {confirmingDelete && (
                <ConfirmDeleteNoteModal
                    isDeleting={deleteMutation.isPending}
                    onClose={() => setConfirmingDelete(false)}
                    onConfirm={() => void handleConfirmDelete()}
                />
            )}
        </div>
    );
};

export default ChartNotesField;
